import {
  SUPPORT_CORRECTIONS,
  SUPPORT_ROSTER_SUBMISSIONS,
  SUPPORT_SCHEDULE_SUBMISSIONS
} from '@maxpreps/shared/constants/supportArticles';
import useTeamContext from '@maxpreps/shared/contexts/TeamContext';
import {
  useIsDesktop,
  useIsScreenSize
} from '@maxpreps/shared/hooks/useIsScreenSize';
import { DefaultDeviceWidth } from '@maxpreps/shared/styles/breakpoint';
import THEME from '@maxpreps/shared/styles/colors';
import Font from '@maxpreps/shared/styles/font';
import CardHeader from '@maxpreps/shared/ui/Card/CardHeader';
import StyledCard from '@maxpreps/shared/ui/Card/StyledCard';
import Carousel from '@maxpreps/shared/ui/Carousel/Carousel';
import { CarouselIndicatorCardFooter } from '@maxpreps/shared/ui/Carousel/CarouselIndicator';
import CarouselProvider from '@maxpreps/shared/ui/Carousel/CarouselProvider';
import {
  iconCorrection,
  iconPhoto,
  iconRoster,
  iconSchedule,
  iconVideo
} from '@maxpreps/shared/ui/Icon/ContributionIcons';
import Icon, { IconType } from '@maxpreps/shared/ui/Icon/Icon';
import StyledText from '@maxpreps/shared/ui/Text/StyledText';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { Context as ResponsiveContext } from 'react-responsive';
import styled from 'styled-components';

const StyledContributeCard = styled(StyledCard)`
  > .carousel {
    overflow: hidden;
  }
`;

const TeamPhotoModal = dynamic(() => import('./TeamPhoto/TeamPhotoModal'), {
  ssr: false
});

enum Tile {
  Schdule,
  Roster,
  TeamPhoto,
  Video,
  Correction
}

interface IContributionTile {
  type: Tile;
  text: string;
  description: string;
  icon: any;
  href?: string;
  enabled?: boolean;
  onClick?: (e: PointerEvent) => void;
}
const CONTRIBUTION_TILES: IContributionTile[] = [
  {
    type: Tile.Schdule,
    text: 'Complete the Schedule',
    description: 'Add missing games to the schedule',
    icon: iconSchedule,
    href: SUPPORT_SCHEDULE_SUBMISSIONS
  },
  {
    type: Tile.Roster,
    text: 'Complete the Roster',
    description: 'Add missing athletes to the roster',
    icon: iconRoster,
    href: SUPPORT_ROSTER_SUBMISSIONS
  },
  {
    type: Tile.TeamPhoto,
    text: 'Add a Team Photo',
    description: 'Give fans a look at the team',
    icon: iconPhoto,
    enabled: false
  },
  {
    type: Tile.Video,
    text: 'Post a Video',
    description: 'Share team highlights with fans & media',
    icon: iconVideo,
    href: 'https://www.maxpreps.com/utility/uploads/video/default.aspx?gendersport=boys,football&schoolid=[teamId]'
  },
  {
    type: Tile.Correction,
    text: 'Make a Correction',
    description: 'Let us know about any issues you see',
    icon: iconCorrection,
    href: SUPPORT_CORRECTIONS
  }
];

const StyledTile = styled.a`
  width: 100%;
  margin: 0 16px;
  border: 1px solid ${THEME.general.border};
  border-radius: 8px;
  display: flex;
  align-items: center;
  padding: 12px;
  margin: auto;
  overflow: hidden;
  cursor: pointer;

  background-color: ${THEME.surface.primary};
  &:hover {
    background-color: ${THEME.surface.tertiary};
  }

  & > .text-container {
    text-align: left;
    margin-left: 12px;
  }
`;

const formatTiles = (tiles: IContributionTile[], teamId: string) =>
  tiles
    .filter(t => t && (t?.enabled ?? true))
    .map(t => ({
      ...t,
      href: t.href?.replace('[teamId]', teamId)
    }));

const ContributionCard: React.FC = () => {
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const teamContext = useTeamContext();
  const { teamPhotoUrl } = teamContext;
  const { teamId } = teamContext.data;
  const [tiles, setTiles] = useState(formatTiles(CONTRIBUTION_TILES, teamId));

  const showOneAndHalf = useIsScreenSize(500);
  const showTwo = useIsScreenSize(700);
  const isDesktop = useIsDesktop();

  const tilesToShow = showTwo ? 2.1 : showOneAndHalf ? 1.5 : 1.1;

  useEffect(() => {
    if (!teamPhotoUrl) {
      const _tiles = CONTRIBUTION_TILES.map(c => {
        if (c.type === Tile.TeamPhoto) {
          return {
            ...c,
            enabled: true,
            onClick: (e: PointerEvent) => setIsPhotoModalOpen(true)
          } as IContributionTile;
        }

        return c;
      });

      setTiles(formatTiles(_tiles, teamId));
    }
  }, [teamPhotoUrl, isPhotoModalOpen, teamId]);

  const slides = tiles.map(a => {
    const { text, description, icon, href, onClick } = a;
    const props = onClick
      ? { as: 'button', type: 'button', onClick }
      : { href, alt: text, target: '_blank' };
    return (
      // @ts-ignore
      <StyledTile key={text} {...props}>
        <Icon
          icon={icon}
          iconType={IconType.Contribution}
          size={32}
          // Need to set explicit mins
          // @ts-ignore
          $minWidth="32px"
          $minHeight="32px"
        />
        <div className="text-container">
          <StyledText
            as="div"
            className={Font.f14_bold_tall}
            $color={THEME.text.primary}
          >
            {text}
          </StyledText>
          <StyledText
            as="div"
            className={Font.f12_tall}
            $color={THEME.text.tertiary}
            $whiteSpace="nowrap"
          >
            {description}
          </StyledText>
        </div>
      </StyledTile>
    );
  });

  return (
    <>
      <StyledContributeCard>
        <CardHeader title="Contribute to the Team" />
        <CarouselProvider
          naturalSlideWidth={320}
          naturalSlideHeight={60}
          numVisibleSlides={tilesToShow}
          numTotalSlides={slides.length}
        >
          <Carousel naturalSlideHeight={60} slides={slides} yPadding={12} />
          <CarouselIndicatorCardFooter />
        </CarouselProvider>
      </StyledContributeCard>
      {isPhotoModalOpen && (
        /* Ugly hack - need to override right-rail responsive context */
        <ResponsiveContext.Provider
          value={isDesktop ? DefaultDeviceWidth.Desktop : undefined}
        >
          <TeamPhotoModal closeModal={() => setIsPhotoModalOpen(false)} />
        </ResponsiveContext.Provider>
      )}
    </>
  );
};

export default ContributionCard;
